import { useEffect, useRef, useState, type SetStateAction } from 'react'
import { useCallbackEvent } from './use-callback-event'

type Callback<State> = (state: State) => void
type SetStateWithCallback<State> = (state: SetStateAction<State>, callback?: Callback<State>) => void
/**
 * useStateWithCallback is a custom hook that extends the useState hook by adding a callback function that is called after the state is updated.
 */
export function useStateWithCallback<State>(initialState: State): [State, SetStateWithCallback<State>] {
  const [state, setState] = useState<State>(initialState)
  const callbacks = useRef<Callback<State>[]>([])

  useEffect(() => {
    if (callbacks.current) {
      callbacks.current.forEach(func => func(state))

      callbacks.current = []
    }
  }, [state])

  const setStateWithCallback: SetStateWithCallback<State> = useCallbackEvent((state, callback) => {
    setState(state)
    if (callback) {
      callbacks.current.push(callback)
    }
  })

  return [state, setStateWithCallback]
}
